import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  useTheme,
  alpha,
  Divider,
} from '@mui/material'
import {
  Hotel as HotelIcon,
  LocationOn as LocationIcon,
  Restaurant as RestaurantIcon,
  LocalParking as ParkingIcon,
  Wifi as WifiIcon,
  Security as SecurityIcon,
  SupportAgent as SupportIcon,
  CleaningServices as CleaningIcon,
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import LegalHero from './legal/LegalHero'

const MotionBox = motion(Box)
const MotionCard = motion(Card)

const features = [
  {
    icon: <HotelIcon />,
    title: 'Premium Rooms',
    description: 'Luxurious rooms with modern amenities and comfortable furnishings.',
  },
  {
    icon: <LocationIcon />,
    title: 'Prime Location',
    description: 'Located near Light House, walking distance from Puri Beach and Jagannath Temple.',
  },
  {
    icon: <RestaurantIcon />,
    title: 'Dining',
    description: 'Multi-cuisine restaurant serving delicious local and international dishes.',
  },
  {
    icon: <ParkingIcon />,
    title: 'Parking',
    description: 'Secure parking facility available for guests with 24/7 surveillance.',
  },
  {
    icon: <WifiIcon />,
    title: 'Free Wi-Fi',
    description: 'High-speed internet connectivity throughout the property.',
  },
  {
    icon: <SecurityIcon />,
    title: 'Security',
    description: '24/7 security with CCTV surveillance for your peace of mind.',
  },
  {
    icon: <SupportIcon />,
    title: 'Guest Support',
    description: 'Dedicated guest support team available round the clock.',
  },
  {
    icon: <CleaningIcon />,
    title: 'Housekeeping',
    description: 'Daily housekeeping service with strict hygiene protocols.',
  },
]

const About = () => {
  const theme = useTheme()

  const FeatureCard = ({ icon, title, description, index }: any) => (
    <Grid item xs={12} sm={6} md={3}>
      <MotionCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 * index }}
        elevation={0}
        sx={{
          height: '100%',
          bgcolor: 'transparent',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderColor: 'primary.main',
          },
        }}
      >
        <CardContent>
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Box
              sx={{
                color: 'primary.main',
                fontSize: '2.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Stack>
        </CardContent>
      </MotionCard>
    </Grid>
  )

  return (
    <Box>
      <LegalHero
        title="About Bisworupa Inn"
        subtitle="Experience luxury and comfort in the heart of Puri"
      />

      {/* Story Section */}
      <Container maxWidth="xl">
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <MotionBox
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Typography
                  variant="h3"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Our Story
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: 'text.secondary', mb: 3 }}>
                  Established in 2025, Bisworupa Inn has become synonymous with exceptional hospitality in Puri. 
                  Our journey began with a vision to create a haven of comfort and luxury for pilgrims and tourists 
                  visiting the holy city of Puri.
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: 'text.secondary' }}>
                  Located near the iconic Light House and just minutes away from the revered Jagannath Temple, 
                  we offer our guests the perfect blend of modern amenities and traditional hospitality. Our 
                  commitment to service excellence and attention to detail has earned us the trust and loyalty 
                  of guests from around the world.
                </Typography>
              </MotionBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MotionBox
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                sx={{
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    bottom: 20,
                    left: 20,
                    border: `2px solid ${theme.palette.primary.main}`,
                    borderRadius: 2,
                    zIndex: 0,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/images/hotel-front.jpeg"
                  alt="Bisworupa Inn Front View"
                  sx={{
                    width: '100%',
                    height: 400,
                    objectFit: 'cover',
                    borderRadius: 2,
                    position: 'relative',
                    zIndex: 1,
                  }}
                />
              </MotionBox>
            </Grid>
          </Grid>
        </Box>

        {/* Mission & Values */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 6,
              background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Our Values
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                sx={{ textAlign: 'center' }}
              >
                <Typography variant="h5" gutterBottom color="primary">
                  Exceptional Service
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  We go above and beyond to ensure every guest feels valued and cared for during their stay.
                </Typography>
              </MotionBox>
            </Grid>
            <Grid item xs={12} md={4}>
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                sx={{ textAlign: 'center' }}
              >
                <Typography variant="h5" gutterBottom color="primary">
                  Cultural Connection
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  We help our guests experience the rich cultural heritage of Puri while enjoying modern comforts.
                </Typography>
              </MotionBox>
            </Grid>
            <Grid item xs={12} md={4}>
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                sx={{ textAlign: 'center' }}
              >
                <Typography variant="h5" gutterBottom color="primary">
                  Sustainable Practices
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  We are committed to eco-friendly practices while maintaining the highest standards of luxury.
                </Typography>
              </MotionBox>
            </Grid>
          </Grid>
        </Box>

        {/* Features Section */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 6,
              background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Hotel Features
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <FeatureCard key={feature.title} {...feature} index={index} />
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  )
}

export default About 